import { createEmailHistoryApiInstance, getForwardedHost } from 'utils/emailHistory/apiUtils';

/**
 * Email History API Client - Campaigns
 * Base URL: /api3.2/emailHistory
 */

// Get the email history API instance
const emailHistoryApi = createEmailHistoryApiInstance('/api3.2/emailHistory');

/**
 * List all campaigns with pagination and filtering
 * @param {Object} params - Query parameters
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.pageSize=25] - Items per page
 * @param {string} [params.sortBy='lastSendDate'] - Field to sort by
 * @param {string} [params.sortOrder='desc'] - Sort direction ('asc' or 'desc')
 * @param {string} [params.search] - Search by campaign name
 * @param {string} [params.dateFrom] - Filter by last send date (ISO 8601)
 * @param {string} [params.dateTo] - Filter by last send date (ISO 8601)
 * @returns {Promise<{rows: Array, totalRows: number, page: number, pageSize: number}>}
 */
export async function fetchCampaigns(params = {}) {
  const {
    page = 0,
    pageSize = 25,
    sortBy = 'lastSendDate',
    sortOrder = 'desc',
    search = null,
    dateFrom = null,
    dateTo = null,
  } = params;

  const queryParams = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortOrder,
  });

  if (search && search.trim()) {
    queryParams.set('search', search.trim());
  }

  if (dateFrom) {
    queryParams.set('dateFrom', dateFrom);
  }

  if (dateTo) {
    queryParams.set('dateTo', dateTo);
  }

  try {
    const { data } = await emailHistoryApi.get(`/campaigns?${queryParams.toString()}`, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch campaigns');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
}

/**
 * Get detailed statistics for a specific campaign
 * @param {number} campaignId - The campaign ID
 * @returns {Promise<Object>} Campaign details with trend data
 */
export async function fetchCampaignDetails(campaignId) {
  try {
    const { data } = await emailHistoryApi.get(`/campaigns/${campaignId}`, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch campaign details');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching campaign details:', error);
    throw error;
  }
}

/**
 * Get paginated list of bulk emails within a campaign
 * @param {number} campaignId - The campaign ID
 * @param {Object} params - Query parameters
 * @returns {Promise<{rows: Array, totalRows: number, page: number, pageSize: number}>}
 */
export async function fetchCampaignEmails(campaignId, params = {}) {
  const {
    page = 0,
    pageSize = 25,
    sortBy = 'sendDate',
    sortOrder = 'desc',
    dateFrom = null,
    dateTo = null,
  } = params;

  const queryParams = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortOrder,
  });

  if (dateFrom) {
    queryParams.set('dateFrom', dateFrom);
  }

  if (dateTo) {
    queryParams.set('dateTo', dateTo);
  }

  try {
    const { data } = await emailHistoryApi.get(`/campaigns/${campaignId}/emails?${queryParams.toString()}`, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch campaign emails');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching campaign emails:', error);
    throw error;
  }
}

